/**
 * AI-assisted Legacy Book export workflow — prepare, review, then download PDF.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Loader2, Eye, Download, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { aiLegacyBookApi } from '../../utils/aiLegacyBook';
import { AiLegacyBookReviewModal } from './AiLegacyBookReviewModal';

export function AiLegacyBookExportPanel({
  treeId,
  canExport = false,
  peopleCount = 0,
  onExportPdf,
  exportingPdf = false,
  compact = false,
}) {
  const [draft, setDraft] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const loadDraft = useCallback(async () => {
    if (!treeId) return;
    setLoadingDraft(true);
    try {
      const data = await aiLegacyBookApi.getDraft(treeId);
      setDraft(data.draft || null);
    } catch {
      setDraft(null);
    } finally {
      setLoadingDraft(false);
    }
  }, [treeId]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const handlePrepare = async () => {
    if (!treeId) return;
    if (peopleCount === 0) {
      toast.message('A few people and a couple of stories help the draft take shape.');
      return;
    }
    setPreparing(true);
    try {
      const data = await aiLegacyBookApi.prepare(treeId);
      setDraft(data.draft);
      toast.success(
        data.draft?.ai_polished
          ? 'Book draft prepared with AI polish — please review before export.'
          : 'Book draft prepared from your archive — please review before export.',
      );
      setReviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not prepare book draft.');
    } finally {
      setPreparing(false);
    }
  };

  const handleReviewSaved = (updated) => {
    setDraft(updated);
  };

  const handleMarkReviewed = async () => {
    if (!treeId) return;
    try {
      const data = await aiLegacyBookApi.markReviewed(treeId);
      setDraft(data.draft);
      toast.success('Draft marked as reviewed. You can download your keepsake PDF.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save review.');
    }
  };

  const hasDraft = Boolean(draft?.sections);
  const reviewed = Boolean(draft?.reviewed);
  const canPrepare = peopleCount > 0 && canExport;

  return (
    <>
      <div
        className={`rounded-2xl border border-[#c9a961]/35 bg-gradient-to-br from-[#fffdf9] via-[#fffaf3] to-[#fff5e8]/70 ${
          compact ? 'p-4' : 'p-5 sm:p-6'
        }`}
        data-testid="ai-legacy-book-export-panel"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#c9a961]/15">
            <Sparkles className="h-4 w-4 text-[#8b7355]" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-serif text-base font-medium text-[#1c1917]">Preview your legacy book</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Prepare with AI, review before export, then download your keepsake PDF.
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-[#6c6258]">
              AI helps organise and polish what you have preserved. You stay in control before anything is exported.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                className="rounded-full"
                disabled={!canPrepare || preparing || loadingDraft}
                onClick={handlePrepare}
                data-testid="ai-book-prepare-btn"
              >
                {preparing ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                    Prepare with AI
                  </>
                )}
              </Button>

              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={!hasDraft}
                onClick={() => setReviewOpen(true)}
                data-testid="ai-book-review-btn"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" />
                Review before export
              </Button>

              <Button
                type="button"
                size="sm"
                variant={reviewed ? 'default' : 'outline'}
                className="rounded-full"
                disabled={!canExport || !onExportPdf || exportingPdf || !reviewed}
                onClick={() => onExportPdf?.('keepsake')}
                data-testid="ai-book-download-btn"
                title={!reviewed ? 'Review and accept your draft before final export' : undefined}
              >
                {exportingPdf ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Creating PDF…
                  </>
                ) : (
                  <>
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Download keepsake PDF
                  </>
                )}
              </Button>
            </div>

            {!canPrepare && peopleCount === 0 ? (
              <p className="mt-3 text-[11px] text-muted-foreground italic">
                Add a few people and memories first — then prepare your book draft.
              </p>
            ) : null}

            {hasDraft && !reviewed ? (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-amber-800/90">
                <Eye className="h-3.5 w-3.5 shrink-0" />
                Draft ready — review and accept sections before final export.
              </p>
            ) : null}

            {reviewed ? (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Draft reviewed — download uses your archive keepsake export.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <AiLegacyBookReviewModal
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        treeId={treeId}
        draft={draft}
        onDraftUpdated={handleReviewSaved}
        onMarkReviewed={handleMarkReviewed}
      />
    </>
  );
}

export default AiLegacyBookExportPanel;
